import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Loader2, Save, User, Mail, Building2, Briefcase, Phone, Shield, Calendar } from "lucide-react";
import { format } from "date-fns";

const Profile = () => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { data: session } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session;
    },
  });

  const { data: profile, refetch, isLoading: profileLoading } = useQuery({
    queryKey: ["profile", session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session!.user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const { data: userRoles } = useQuery({
    queryKey: ["user-roles", session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session!.user.id);

      if (error) throw error;
      return data || [];
    },
  });

  const handleSaveProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const formData = new FormData(e.currentTarget);
      const updates = {
        name: formData.get("name") as string,
        title: formData.get("title") as string,
        institution: formData.get("institution") as string,
        department: formData.get("department") as string,
      };

      // Upsert profile (create if doesn't exist, update if it does)
      const { error } = await supabase
        .from("profiles")
        .upsert({
          id: session!.user.id,
          ...updates,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });

      setIsEditing(false);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "super_admin":
        return "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20";
      case "compliance_officer":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20";
      case "athletic_director":
        return "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20";
      case "coach":
        return "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20";
      default:
        return "bg-secondary text-secondary-foreground border-secondary";
    }
  };

  const formatRole = (role?: string): string => {
    if (!role) return "Unknown Role";
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!session || profileLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // Show profile creation prompt if no profile exists
  if (!profile) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fade-in">
          <Card className="border-2 border-primary/20">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-4 rounded-full bg-primary/10">
                  <User className="h-12 w-12 text-primary" />
                </div>
              </div>
              <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
              <CardDescription>
                Let's set up your profile to get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveProfile} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="h-4 w-4 text-primary" />
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="title" className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-primary" />
                      Job Title
                    </Label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      placeholder="Compliance Officer"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="institution" className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" />
                      Institution
                    </Label>
                    <Input
                      id="institution"
                      name="institution"
                      type="text"
                      placeholder="University Name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="department" className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" />
                      Department
                    </Label>
                    <Input
                      id="department"
                      name="department"
                      type="text"
                      placeholder="Athletics Department"
                    />
                  </div>
                </div>

                <div className="flex justify-center">
                  <Button type="submit" disabled={isSaving} size="lg">
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Profile...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Create Profile
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <User className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">My Profile</h1>
            <p className="text-muted-foreground mt-1">
              Manage your personal information and account settings
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Profile Summary Card */}
          <Card className="md:col-span-1 border-2">
            <CardHeader className="text-center pb-4">
              <div className="flex justify-center mb-4">
                <Avatar className="h-24 w-24 border-4 border-primary/10">
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary font-bold">
                    {getInitials(profile?.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <CardTitle className="text-xl">{profile?.name || "User"}</CardTitle>
              <CardDescription className="flex items-center justify-center gap-1 mt-2">
                <Mail className="h-3 w-3" />
                {session?.user?.email}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Separator />
              
              {userRoles && userRoles.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Shield className="h-4 w-4" />
                    Roles
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {userRoles
                      ?.filter((role) => role?.role)
                      .map((role, idx) => (
                        <span
                          key={idx}
                          className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(
                            role.role
                          )}`}
                        >
                          {formatRole(role.role)}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Member Since
                </div>
                <p className="text-sm">
                  {format(new Date(profile?.created_at || Date.now()), "MMMM d, yyyy")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Profile Details Card */}
          <Card className="md:col-span-2 border-2 hover:border-primary/20 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-primary" />
                <CardTitle>Personal Information</CardTitle>
              </div>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Profile
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User className="h-4 w-4 text-primary" />
                        Full Name
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        defaultValue={profile?.name || ""}
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="title" className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-primary" />
                        Job Title
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        type="text"
                        defaultValue={profile?.title || ""}
                        placeholder="Compliance Officer"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="institution" className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        Institution
                      </Label>
                      <Input
                        id="institution"
                        name="institution"
                        type="text"
                        defaultValue={profile?.institution || ""}
                        placeholder="University Name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="department" className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary" />
                        Department
                      </Label>
                      <Input
                        id="department"
                        name="department"
                        type="text"
                        defaultValue={profile?.department || ""}
                        placeholder="Athletics Department"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditing(false)}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <User className="h-4 w-4" />
                        Full Name
                      </div>
                      <p className="text-sm font-medium">
                        {profile?.name || "Not set"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Briefcase className="h-4 w-4" />
                        Job Title
                      </div>
                      <p className="text-sm font-medium">
                        {profile?.title || "Not set"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                        Institution
                      </div>
                      <p className="text-sm font-medium">
                        {profile?.institution || "Not set"}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                        Department
                      </div>
                      <p className="text-sm font-medium">
                        {profile?.department || "Not set"}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      Email Address
                    </div>
                    <p className="text-sm font-medium">{session?.user?.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Contact support to change your email address
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Account Security Card */}
        <Card className="border-2 hover:border-primary/20 transition-colors">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle>Account Security</CardTitle>
            </div>
            <CardDescription>
              Manage your password and security settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="space-y-0.5">
                <p className="font-medium">Password</p>
                <p className="text-sm text-muted-foreground">
                  Last changed: Not available
                </p>
              </div>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { error } = await supabase.auth.resetPasswordForEmail(
                      session.user.email!,
                      {
                        redirectTo: `${window.location.origin}/auth`,
                      }
                    );

                    if (error) throw error;

                    toast({
                      title: "Password Reset Email Sent",
                      description: "Check your email for password reset instructions",
                    });
                  } catch (error: any) {
                    toast({
                      title: "Error",
                      description: error.message,
                      variant: "destructive",
                    });
                  }
                }}
              >
                Reset Password
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="space-y-0.5">
                <p className="font-medium">Account ID</p>
                <p className="text-xs text-muted-foreground font-mono">
                  {session?.user?.id}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
